import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../components/auth/AuthContext';
import { userService } from '../../services/api/userService';
import { auth, multiFactor } from '../../config/firebase';
import PasswordChangeForm from '../../components/auth/PasswordChangeForm';
import './UserProfileModal.css';

/** Map scope keys to profile i18n label keys. */
const SCOPE_LABEL_MAP: Record<string, string> = {
  trades: 'mcpScopeTrades',
  accounting: 'mcpScopeAccounting',
  cashflows: 'mcpScopeCashflows',
  valuation: 'mcpScopeValuation',
  confirmations: 'mcpScopeConfirmations',
  modifications: 'mcpScopeModifications',
  approvals: 'mcpScopeApprovals',
  benchmarks: 'mcpScopeBenchmarks',
};

const DEV_ADMIN_UID = 'Y32pQhE7GgdIt4jfF8YcagdVUwl1';

interface UserProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const UserProfileModal: React.FC<UserProfileModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const { user } = useAuth();

  const profile = user?.profile;

  // Check Firebase Auth directly for enrolled TOTP — this is the source of truth,
  // not the Firestore flags which can be out of sync.
  const firebaseUser = auth.currentUser;
  const hasTotpEnrolled = firebaseUser
    ? multiFactor(firebaseUser).enrolledFactors.some(f => f.factorId === 'totp')
    : false;
  const isMfaUser = hasTotpEnrolled;

  // === Non-MFA user: reset email state ===
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState('');
  const [resetError, setResetError] = useState('');
  const [cooldownSeconds, setCooldownSeconds] = useState(0);

  // === MCP self-service: fresh data + copy feedback ===
  const [mcpKeyId, setMcpKeyId] = useState<string | undefined>(profile?.mcpKeyId);
  const [mcpKeyIsActive, setMcpKeyIsActive] = useState<boolean | undefined>(profile?.mcpKeyIsActive);
  const [mcpScopes, setMcpScopes] = useState<string[] | undefined>(profile?.mcpScopes);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const handleCopyField = useCallback(async (value: string, fieldName: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(null), 2000);
    } catch { /* clipboard not available */ }
  }, []);

  // === Dev admin: client switcher state ===
  const isDevAdmin = firebaseUser?.uid === DEV_ADMIN_UID;
  const [availableClients, setAvailableClients] = useState<{ id: string; name: string }[]>([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [currentClientId, setCurrentClientId] = useState('');
  const [clientSwitchLoading, setClientSwitchLoading] = useState(false);
  const [clientSwitchMessage, setClientSwitchMessage] = useState('');

  // Refresh MCP fields from backend when modal opens (admin may have changed them)
  useEffect(() => {
    if (!isOpen) return;
    const refreshMcp = async () => {
      try {
        const res = await userService.getMyProfile();
        if (res.success && res.data) {
          setMcpKeyId(res.data.mcpKeyId);
          setMcpKeyIsActive(res.data.mcpKeyIsActive);
          setMcpScopes(res.data.mcpScopes);
        }
      } catch { /* non-critical */ }
    };
    refreshMcp();
  }, [isOpen]);

  // Reset form state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setResetLoading(false);
      setResetSuccess('');
      setResetError('');
      setClientSwitchMessage('');
    }
  }, [isOpen]);

  // Load available clients for dev admin
  useEffect(() => {
    if (!isOpen || !isDevAdmin) return;
    const loadClients = async () => {
      try {
        const response = await userService.getAvailableClients();
        if (response.success && response.data) {
          setAvailableClients(response.data.availableClients);
          setCurrentClientId(response.data.currentClientId);
          setSelectedClientId(response.data.currentClientId);
        }
      } catch (e) {
        console.error('Failed to load available clients:', e);
      }
    };
    loadClients();
  }, [isOpen, isDevAdmin]);

  const handleSwitchClient = async () => {
    if (!selectedClientId || selectedClientId === currentClientId) return;
    setClientSwitchLoading(true);
    setClientSwitchMessage('');
    try {
      await userService.switchClient(selectedClientId);
      setCurrentClientId(selectedClientId);
      setClientSwitchMessage(t('profile.clientSwitcher.success'));
    } catch (e) {
      console.error('Failed to switch client:', e);
      setClientSwitchMessage(t('profile.clientSwitcher.error'));
    } finally {
      setClientSwitchLoading(false);
    }
  };

  // Cooldown timer
  useEffect(() => {
    if (cooldownSeconds <= 0) return;
    const timer = setTimeout(() => setCooldownSeconds(cooldownSeconds - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldownSeconds]);

  const getRoleDisplay = (role?: string) => {
    if (!role) return '—';
    const roleName = role.includes('/') ? role.split('/').pop() : role;
    return t(`userMenu.roles.${roleName}`) || roleName;
  };

  // === Non-MFA user: request password reset email ===
  const handleRequestReset = async () => {
    setResetError('');
    setResetSuccess('');
    setResetLoading(true);

    try {
      const response = await userService.requestPasswordReset();
      if (response.success && response.data) {
        setResetSuccess(t('profile.resetEmail.success', { email: response.data.email }));
        setCooldownSeconds(60);
      } else {
        setResetError(t('profile.resetEmail.error'));
      }
    } catch {
      setResetError(t('profile.resetEmail.error'));
    } finally {
      setResetLoading(false);
    }
  };

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="profile-modal-backdrop" onClick={handleBackdropClick}>
      <div className="profile-modal">
        <div className="profile-modal-header">
          <h2 className="profile-modal-title">{t('profile.title')}</h2>
          <button className="profile-modal-close" onClick={onClose}>
            ×
          </button>
        </div>

        {profile && (
          <div className="profile-modal-body">
            {/* Profile Information Section */}
            <div className="profile-info-section">
              <div className="profile-info-left">
                <div className="profile-field">
                  <span className="profile-label">{t('profile.name')}</span>
                  <span className="profile-value">{profile.fullName}</span>
                </div>
                <div className="profile-field">
                  <span className="profile-label">{t('profile.email')}</span>
                  <span className="profile-value">{profile.email}</span>
                </div>
                <div className="profile-field">
                  <span className="profile-label">{t('profile.jobTitle')}</span>
                  <span className="profile-value">
                    {profile.jobTitle || t('profile.jobTitleEmpty')}
                  </span>
                </div>
                <div className="profile-field">
                  <span className="profile-label">{t('profile.role')}</span>
                  <span className="profile-value">{getRoleDisplay(profile.primaryRole)}</span>
                </div>
              </div>

              <div className="profile-info-right">
                <div className="profile-toggle-field">
                  <span className="profile-toggle-label">{t('profile.mfaStatus')}</span>
                  <div className={`profile-toggle ${hasTotpEnrolled ? 'toggle-on' : 'toggle-off'}`}>
                    <div className="toggle-track">
                      <div className="toggle-thumb" />
                    </div>
                    <span className="toggle-text">
                      {hasTotpEnrolled ? t('profile.mfaEnabled') : t('profile.mfaNotEnabled')}
                    </span>
                  </div>
                </div>
                <div className="profile-toggle-field">
                  <span className="profile-toggle-label">{t('profile.canSignDocuments')}</span>
                  <div className={`profile-toggle ${profile.canSignDocuments ? 'toggle-on' : 'toggle-off'}`}>
                    <div className="toggle-track">
                      <div className="toggle-thumb" />
                    </div>
                    <span className="toggle-text">
                      {profile.canSignDocuments ? t('profile.yes') : t('profile.no')}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Divider */}
            <div className="profile-section-divider" />

            {/* Password Section — conditional on MFA status */}
            <div className="profile-password-section">
              <h3 className="profile-section-title">{t('profile.changePassword')}</h3>

              {isMfaUser ? (
                /* === MFA User: In-app password change form === */
                <PasswordChangeForm />
              ) : (
                /* === Non-MFA User: Request password reset email === */
                <div className="password-reset-section">
                  <p className="reset-description">{t('profile.resetEmail.description')}</p>

                  {/* Error / Success Messages */}
                  {resetError && <div className="password-message password-error">{resetError}</div>}
                  {resetSuccess && <div className="password-message password-success">{resetSuccess}</div>}

                  <button
                    className="password-submit-btn"
                    onClick={handleRequestReset}
                    disabled={resetLoading || cooldownSeconds > 0}
                  >
                    {resetLoading
                      ? t('profile.resetEmail.sending')
                      : cooldownSeconds > 0
                        ? t('profile.resetEmail.cooldown', { seconds: cooldownSeconds })
                        : t('profile.resetEmail.button')}
                  </button>
                </div>
              )}
            </div>

            {/* MCP Access Section (KAN-275) — only for users with an MCP key */}
            {mcpKeyId && (
              <>
                <div className="profile-section-divider" />
                <div className="profile-mcp-section">
                  <div className="profile-mcp-header">
                    <h3 className="profile-section-title">{t('profile.mcpAccess')}</h3>
                    <span className={`profile-mcp-badge ${mcpKeyIsActive === false ? 'profile-mcp-badge-disabled' : 'profile-mcp-badge-active'}`}>
                      {mcpKeyIsActive === false ? t('profile.mcpStatusDisabled') : t('profile.mcpStatusActive')}
                    </span>
                  </div>

                  {/* Scopes */}
                  <div className="profile-mcp-row">
                    <span className="profile-mcp-label">{t('profile.mcpPermissions')}</span>
                    <span className="profile-mcp-value">
                      {mcpScopes && mcpScopes.length > 0
                        ? mcpScopes.map(s => t(`profile.${SCOPE_LABEL_MAP[s] || s}`)).join(', ')
                        : t('profile.mcpNoScopes')}
                    </span>
                  </div>

                  {/* Connection details */}
                  <div className="profile-mcp-connection">
                    <span className="profile-mcp-label">{t('profile.mcpConnection')}</span>
                    {[
                      { label: 'MCP_URL', value: (import.meta as any).env?.VITE_MCP_URL || 'http://127.0.0.1:8080/mcp' },
                      { label: 'FXP_CLIENT_ID', value: profile.organization?.id || '' },
                      { label: 'FXP_KEY_ID', value: mcpKeyId },
                    ].map(({ label, value }) => (
                      <div key={label} className="profile-mcp-credential-row">
                        <span className="profile-mcp-credential-label">{label}</span>
                        <span className="profile-mcp-credential-value">{value}</span>
                        <button
                          className="profile-mcp-copy-icon"
                          onClick={() => handleCopyField(value, label)}
                          title={label}
                        >
                          {copiedField === label ? '✓' : '⧉'}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* Dev Admin: Client Switcher */}
            {isDevAdmin && availableClients.length > 0 && (
              <>
                <div className="profile-section-divider" />
                <div className="profile-client-switcher">
                  <h3 className="profile-section-title">{t('profile.clientSwitcher.title')}</h3>
                  <div className="client-switcher-row">
                    <select
                      className="client-switcher-select"
                      value={selectedClientId}
                      onChange={(e) => setSelectedClientId(e.target.value)}
                    >
                      {availableClients.map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                    <button
                      className="client-switcher-btn"
                      onClick={handleSwitchClient}
                      disabled={clientSwitchLoading || selectedClientId === currentClientId}
                    >
                      {clientSwitchLoading ? '...' : 'OK'}
                    </button>
                  </div>
                  {clientSwitchMessage && (
                    <div className={`password-message ${clientSwitchMessage === t('profile.clientSwitcher.success') ? 'password-success' : 'password-error'}`}>
                      {clientSwitchMessage}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default UserProfileModal;
